import { useState } from 'react';
import { View, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import { Feather } from '@expo/vector-icons';
import Toast from 'react-native-root-toast';

import { fonts } from '../style/fonts';
import { useResponsiveStyles } from '../hooks';
import { generateTemporaryPassword } from '../util/user';
import AppStyles from '../AppStyles';
import Util from '../util/util';

import CustomText from './CustomText';
import AppTextInput from './AppTextInput.js';
import { useTranslation } from '../i18n';


export default function ResetUserPassword({ user, organization, postUpdateAction, fullWidth = false }) {
  const { t } = useTranslation('admin');
  const { screenWidth } = useResponsiveStyles();
  const scale = screenWidth < 900 ? 0.9 : 1;
  const [password, setPassword] = useState('');
  const [generated, setGenerated] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const updatePassword = (newPassword) => {
    setPassword(newPassword);
    postUpdateAction(user, newPassword);
  };

  const handleGeneratePassword = async () => {
    const newPassword = generateTemporaryPassword();
    setIsResetting(true);

    try {
      // Call API to reset password immediately
      await Util.post('UserManagement', '/users/reset', {
        body: {
          username: user,
          email: user,
          organization: organization,
          password: newPassword,
        },
      });

      // Send email notification
      Util.sendEmailNotification(
        '',
        `Your username is ${user} and temporary password is ${newPassword}`,
        'passwordReset',
        organization,
        user
      );

      setGenerated(true);
      postUpdateAction(user, newPassword);

      Toast.show(t('resetPassword.sent'), {
        backgroundColor: AppStyles.colorSet.success,
        position: Toast.positions.BOTTOM,
        duration: 2500,
        textColor: '#fff',
      });
    } catch (error) {
      Toast.show(t('resetPassword.failed'), {
        backgroundColor: AppStyles.colorSet.error,
        position: Toast.positions.BOTTOM,
        duration: 2500,
        textColor: '#fff',
      });
    }

    setIsResetting(false);
  };

  // Use full width when in modal context, otherwise use compact width for table
  const boxWidth = fullWidth ? '100%' : 150 * scale;

  // In production, show only the generate button
  // In dev mode, show text input (optional) + generate button
  if (typeof __DEV__ !== 'undefined' && !__DEV__) {
    // Production: Just a button to generate password
    return (
      <Pressable
        onPress={handleGeneratePassword}
        disabled={isResetting}
        style={({ pressed }) => [
          styles.generateButton,
          { width: boxWidth },
          pressed && { opacity: 0.8 },
          isResetting && { opacity: 0.6 },
        ]}
      >
        {isResetting ? (
          <ActivityIndicator size="small" color="#000" style={{ marginRight: 6 }} />
        ) : (
          <Feather name={generated ? 'check' : 'refresh-cw'} size={14} color="#000" style={{ marginRight: 6 }} />
        )}
        <CustomText size={12} font="medium">
          {isResetting ? t('resetPassword.sending') : generated ? t('resetPassword.emailSent') : t('resetPassword.resetPassword')}
        </CustomText>
      </Pressable>
    );
  }

  // Dev mode: Text input + generate button
  return (
    <View style={[styles.devContainer, { width: boxWidth }]}>
      <AppTextInput
        value={password}
        onChangeText={updatePassword}
        leftIcon="lock"
        placeholder={t('resetPassword.passwordPlaceholder')}
        autoCapitalize="none"
        autoCorrect={false}
        secureTextEntry
        textContentType="password"
        autoComplete="new-password"
        boxStyle={styles.input}
        textStyle={styles.inputText}
      />
      <Pressable
        onPress={handleGeneratePassword}
        style={({ pressed }) => [
          styles.generateButtonSmall,
          pressed && { opacity: 0.8 },
        ]}
      >
        <Feather name="refresh-cw" size={14} color="#000" />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  generateButton: {
    flexDirection: 'row',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    borderRadius: AppStyles.borderRadii.md,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginVertical: 10,
  },
  devContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 10,
  },
  input: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: AppStyles.borderRadii.md,
    flexDirection: 'row',
    padding: 5,
    marginRight: 8,
  },
  inputText: {
    width: '100%',
    fontSize: 12,
    color: AppStyles.colorSet.textPrimary,
    fontFamily: fonts.POPPINS.medium,
  },
  generateButtonSmall: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
    borderRadius: AppStyles.borderRadii.sm,
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
